import { useCallback, useState } from "react";
import type { FormPage } from "../types/form";

export interface UseFormPagesResult {
  currentPage: FormPage;
  currentPageIndex: number;
  furthestVisitedIndex: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  isSinglePage: boolean;
  goNext: () => void;
  goPrevious: () => void;
  goToPage: (index: number) => void;
}

export function useFormPages(pages: FormPage[]): UseFormPagesResult {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [furthestVisitedIndex, setFurthestVisitedIndex] = useState(0);

  const currentPage = pages[currentPageIndex] ?? pages[0];
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === pages.length - 1;
  const isSinglePage = pages.length === 1;

  const advanceTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(next, pages.length - 1));
      setCurrentPageIndex(clamped);
      setFurthestVisitedIndex((prev) => Math.max(prev, clamped));
    },
    [pages.length],
  );

  const goNext = useCallback(() => {
    advanceTo(currentPageIndex + 1);
  }, [advanceTo, currentPageIndex]);

  const goPrevious = useCallback(() => {
    setCurrentPageIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goToPage = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, pages.length - 1));
      setCurrentPageIndex(clamped);
    },
    [pages.length],
  );

  return {
    currentPage,
    currentPageIndex,
    furthestVisitedIndex,
    isFirstPage,
    isLastPage,
    isSinglePage,
    goNext,
    goPrevious,
    goToPage,
  };
}
